import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {getMyCarDetail} from '@di-shared/api/cci/auth';
import {diApiEu} from '@di-shared/api/di';
import {ApiResponseSafetyScoreDetailResponse} from '@di-shared/api/di/__generated_eu';
import {DEFAULT_CACHE_TIME, ERROR, UNIT} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQueries} from '@tanstack/react-query';

export interface SafeDrivingSectionVdspProps {
  vin: string;
}

function SafeDrivingSectionVdsp(props: SafeDrivingSectionVdspProps) {
  return (
    <ErrorBoundary fallback={<SafeDrivingSectionVdspError {...props} />}>
      <Suspense fallback={<SafeDrivingSectionVdspLoading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: SafeDrivingSectionVdspProps) {
  const {t} = useInferableTranslation();

  const [{data: ubiData}, {data: carDetailData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApiEu.safeDriving.detail), vin],
        queryFn: () => diApiEu.safeDriving.detail({vin}),
        select: ({data}: ApiResponseSafetyScoreDetailResponse) => data,
      },
      {
        queryKey: [getQueryKey(getMyCarDetail), vin],
        queryFn: () => getMyCarDetail(vin),
        staleTime: DEFAULT_CACHE_TIME.staleTime,
        gcTime: DEFAULT_CACHE_TIME.gcTime,
      },
    ],
  });

  return (
    <div className="section section_safe_score">
      <div className="section_container">
        <LinkTo
          to={ROUTE_PATHS.SAFE_DRIVING.getFullPath(vin)}
          className="section_container_full_btn"
          onClick={() =>
            emitEventLog({
              event: 'di_driveinfo_interaction',
              data_action: 'view score details',
            })
          }
        />
        <div className="section_container_header">
          <h3 className="body_large_b">{t('ctn_dss_title_eu')}</h3>
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info">
          <strong className="safe_score_title">
            <span className="safe_score_value">{ubiData.safetyDrvScore}</span>
            <span className="safe_score_unit">{t('ctn_dss_$score_points_eu')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('msg_home_dss_compared_01')}</span>
            <span className="score_compare_value">
              {t('ctn_home_dss_compared_02_$n', {
                n: ubiData.safetyDrvScoreDiff > 0 ? '+' + ubiData.safetyDrvScoreDiff : ubiData.safetyDrvScoreDiff,
              })}
            </span>
          </span>

          <div className="safe_score_percent">
            <div className="score_percent_item">
              <span className="score_percent_text">{t('ctn_home_dss_top_percent_$nn')}</span>
              <span className="score_percent_value">
                {ubiData.distribution}
                {UNIT.PERCENT}
              </span>
            </div>
            <div className="score_percent_item">
              <span className="score_percent_text">
                {carDetailData.vehicleModelName} {t('ctn_home_dss_top_percent_$nn')}
              </span>
              <span className="score_percent_value">
                {ubiData.modelDistribution}
                {UNIT.PERCENT}
              </span>
            </div>
          </div>
        </div>
        <div className="safe_score_summary">
          <ul className="score_summary_list">
            <li className="score_summary_item">
              <span className="ic ic_acceleration"></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_sudden_acceleration_eu')}</span>
                <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: ubiData.brstAccCount})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span className="ic ic_deceleration"></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_sudden_deceleration_eu')}</span>
                <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: ubiData.brstDecCount})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span className="ic ic_quick_start"></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_sudden_start_eu')}</span>
                <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: ubiData.strtBrstAccCnt})}</span>
              </div>
            </li>
            <li className="score_summary_item">
              <span className="ic ic_night_driving"></span>
              <div className="score_summary_info">
                <span className="score_summary_title">{t('ctn_dss_night_driving_eu')}</span>
                <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: ubiData.nightDrvCount})}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SafeDrivingSectionVdspError({vin}: SafeDrivingSectionVdspProps) {
  const {t} = useInferableTranslation();
  const {checkGlobalVinWhitelist} = useFirebaseStore(({checkGlobalVinWhitelist}) => ({checkGlobalVinWhitelist}));

  return (
    <section className="section section_safe_score">
      <div className="section_container">
        <div className="section_container_header">
          <h3 className="body_medium_b">{t('ctn_dss_title_eu')}</h3>
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info">
          <strong className="safe_score_title">
            <span className="safe_score_value">{ERROR.DASH}</span>
            <span className="safe_score_unit">{t('ctn_dss_$score_points_eu')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('msg_home_dss_compared_01')}</span>
            <span className={'score_compare_value'}>{t('ctn_home_dss_compared_02_$n', {n: ERROR.DASH})}</span>
          </span>
          <div className="safe_score_percent">
            <span className="score_percent_item">
              <span className="score_percent_text">{t('ctn_home_dss_top_percent_$nn')}</span>
              <span className="score_percent_value">{`${ERROR.DASH}${UNIT.PERCENT}`}</span>
            </span>
            <span className="divider divider_vertical"></span>
            <span className="score_percent_item">
              <span className="score_percent_text">
                {ERROR.DASH} {t('ctn_home_dss_top_percent_$nn')}
              </span>
              <span className="score_percent_value">{`${ERROR.DASH}${UNIT.PERCENT}`}</span>
            </span>
          </div>
        </div>
        <div className="safe_score_summary">
          <ul className="score_summary_list">
            <li className="score_summary_item">
              <span className="ic ic_map_point_up_16"></span>
              <span className="score_summary_title">{t('ctn_dss_sudden_acceleration_eu')}</span>
              <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: ERROR.DASH})}</span>
            </li>
            <li className="score_summary_item">
              <span className="ic ic_map_point_down_16"></span>
              <span className="score_summary_title">{t('ctn_dss_sudden_deceleration_eu')}</span>
              <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: ERROR.DASH})}</span>
            </li>
            <li className="score_summary_item">
              <span className="ic ic_map_point_sudden_16"></span>
              <span className="score_summary_title">{t('ctn_dss_sudden_start_eu')}</span>
              <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: ERROR.DASH})}</span>
            </li>
            <li className="score_summary_item">
              <span className="ic ic_map_point_night_16"></span>
              <span className="score_summary_title">{t('ctn_dss_night_driving_eu')}</span>
              <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: ERROR.DASH})}</span>
            </li>
          </ul>
        </div>
        <LinkTo
          to={ROUTE_PATHS.SAFE_DRIVING.getFullPath(vin)}
          className="section_container_full_btn"
        >
          <span className="blind">{t('T20087')}</span>
        </LinkTo>
      </div>
    </section>
  );
}

function SafeDrivingSectionVdspLoading() {
  const {t} = useInferableTranslation();

  return (
    <section className={clsx('section section_safe_score skeleton')}>
      <div className="section_container">
        <div className="section_container_header skeleton">
          <h3 className="body_medium_b skeleton">{t('ctn_dss_title_eu')}</h3>
          <span
            className="ic ic_arrow_forward_16px skeleton"
            aria-hidden="true"
          ></span>
        </div>
        <div className="safe_score_info skeleton">
          <strong className="safe_score_title">
            <span className="safe_score_value">80</span>
            <span className="safe_score_unit">{t('ctn_dss_$score_points_eu')}</span>
          </strong>
          <span className="safe_score_compare">
            <span className="score_compare_text">{t('msg_home_dss_compared_01')}</span>
            <span className={clsx('score_compare_value')}>{t('ctn_home_dss_compared_02_$n', {n: 30})}</span>
          </span>
          <div className="safe_score_percent">
            <span className="score_percent_item">
              <span className="score_percent_text">{t('ctn_home_dss_top_percent_$nn')}</span>
              <span className="score_percent_value">{`${Math.max(1, 20)}${UNIT.PERCENT}`}</span>
            </span>
            <span className="divider divider_vertical"></span>
            <span className="score_percent_item">
              <span className="score_percent_text">GV70 {t('ctn_home_dss_top_percent_$nn')}</span>
              <span className="score_percent_value">{`${Math.max(1, 20)}${UNIT.PERCENT}`}</span>
            </span>
          </div>
        </div>
        <div className="safe_score_summary">
          <ul className="score_summary_list">
            <li className="score_summary_item skeleton">
              <span className="ic ic_map_point_up_16"></span>
              <span className="score_summary_title">{t('ctn_dss_sudden_acceleration_eu')}</span>
              <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: 5})}</span>
            </li>
            <li className="score_summary_item skeleton">
              <span className="ic ic_map_point_down_16"></span>
              <span className="score_summary_title">{t('ctn_dss_sudden_deceleration_eu')}</span>
              <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: 4})}</span>
            </li>
            <li className="score_summary_item skeleton">
              <span className="ic ic_map_point_sudden_16"></span>
              <span className="score_summary_title">{t('ctn_dss_sudden_start_eu')}</span>
              <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: 3})}</span>
            </li>
            <li className="score_summary_item skeleton">
              <span className="ic ic_map_point_night_16"></span>
              <span className="score_summary_title">{t('ctn_dss_night_driving_eu')}</span>
              <span className="score_summary_value">{t('ctn_dss_n_times_$n_eu', {n: 2})}</span>
            </li>
          </ul>
        </div>
        <a
          role="button"
          className="section_container_full_btn"
        >
          <span className="blind">{t('T20087')}</span>
        </a>
      </div>
    </section>
  );
}

SafeDrivingSectionVdsp.Loading = () => <SafeDrivingSectionVdspLoading />;
export default SafeDrivingSectionVdsp;
